"use client"

import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuShortcut,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
} from "@/components/ui/context-menu"
import {
  Trash2,
  Copy,
  Maximize,
  AlignHorizontalJustifyCenter,
  AlignVerticalJustifyCenter,
  AlignHorizontalJustifyStart,
  AlignHorizontalJustifyEnd,
  AlignVerticalJustifyStart,
  AlignVerticalJustifyEnd,
  MoveUp,
  MoveDown,
  RotateCw,
  RotateCcw,
  Layers,
  ArrowUp,
  ArrowDown,
  FlipHorizontal,
  FlipVertical,
  RefreshCcw,
} from "lucide-react"

interface ImageContextMenuProps {
  children: React.ReactNode
  onDelete: () => void
  onDuplicate: () => void
  onFitToPage: () => void
  onCenterHorizontal: () => void
  onCenterVertical: () => void
  onAlignLeft: () => void
  onAlignRight: () => void
  onAlignTop: () => void
  onAlignBottom: () => void
  onBringToFront: () => void
  onSendToBack: () => void
  onBringForward: () => void
  onSendBackward: () => void
  onRotate90: () => void
  onRotate90CCW: () => void
  onFlipHorizontal?: () => void
  onFlipVertical?: () => void
  onResetSize?: () => void
}

export function ImageContextMenu({
  children,
  onDelete,
  onDuplicate,
  onFitToPage,
  onCenterHorizontal,
  onCenterVertical,
  onAlignLeft,
  onAlignRight,
  onAlignTop,
  onAlignBottom,
  onBringToFront,
  onSendToBack,
  onBringForward,
  onSendBackward,
  onRotate90,
  onRotate90CCW,
  onFlipHorizontal,
  onFlipVertical,
  onResetSize,
}: ImageContextMenuProps) {
  return (
    <ContextMenu>
      {children}
      <ContextMenuContent className="w-56">
        <ContextMenuItem onClick={onFitToPage}>
          <Maximize className="mr-2 h-4 w-4" />
          Fit to Page
          <ContextMenuShortcut>F</ContextMenuShortcut>
        </ContextMenuItem>
        <ContextMenuItem onClick={onDuplicate}>
          <Copy className="mr-2 h-4 w-4" />
          Duplicate
          <ContextMenuShortcut>Ctrl+D</ContextMenuShortcut>
        </ContextMenuItem>

        <ContextMenuSeparator />

        <ContextMenuSub>
          <ContextMenuSubTrigger>
            <AlignHorizontalJustifyCenter className="mr-2 h-4 w-4" />
            Align
          </ContextMenuSubTrigger>
          <ContextMenuSubContent className="w-48">
            <ContextMenuItem onClick={onAlignLeft}>
              <AlignHorizontalJustifyStart className="mr-2 h-4 w-4" />
              Align Left
            </ContextMenuItem>
            <ContextMenuItem onClick={onCenterHorizontal}>
              <AlignHorizontalJustifyCenter className="mr-2 h-4 w-4" />
              Center Horizontal
              <ContextMenuShortcut>H</ContextMenuShortcut>
            </ContextMenuItem>
            <ContextMenuItem onClick={onAlignRight}>
              <AlignHorizontalJustifyEnd className="mr-2 h-4 w-4" />
              Align Right
            </ContextMenuItem>
            <ContextMenuSeparator />
            <ContextMenuItem onClick={onAlignTop}>
              <AlignVerticalJustifyStart className="mr-2 h-4 w-4" />
              Align Top
            </ContextMenuItem>
            <ContextMenuItem onClick={onCenterVertical}>
              <AlignVerticalJustifyCenter className="mr-2 h-4 w-4" />
              Center Vertical
              <ContextMenuShortcut>V</ContextMenuShortcut>
            </ContextMenuItem>
            <ContextMenuItem onClick={onAlignBottom}>
              <AlignVerticalJustifyEnd className="mr-2 h-4 w-4" />
              Align Bottom
            </ContextMenuItem>
          </ContextMenuSubContent>
        </ContextMenuSub>

        <ContextMenuSub>
          <ContextMenuSubTrigger>
            <Layers className="mr-2 h-4 w-4" />
            Layer
          </ContextMenuSubTrigger>
          <ContextMenuSubContent className="w-48">
            <ContextMenuItem onClick={onBringToFront}>
              <ArrowUp className="mr-2 h-4 w-4" />
              Bring to Front
              <ContextMenuShortcut>Ctrl+]</ContextMenuShortcut>
            </ContextMenuItem>
            <ContextMenuItem onClick={onBringForward}>
              <MoveUp className="mr-2 h-4 w-4" />
              Bring Forward
              <ContextMenuShortcut>]</ContextMenuShortcut>
            </ContextMenuItem>
            <ContextMenuItem onClick={onSendBackward}>
              <MoveDown className="mr-2 h-4 w-4" />
              Send Backward
              <ContextMenuShortcut>[</ContextMenuShortcut>
            </ContextMenuItem>
            <ContextMenuItem onClick={onSendToBack}>
              <ArrowDown className="mr-2 h-4 w-4" />
              Send to Back
              <ContextMenuShortcut>Ctrl+[</ContextMenuShortcut>
            </ContextMenuItem>
          </ContextMenuSubContent>
        </ContextMenuSub>

        <ContextMenuSub>
          <ContextMenuSubTrigger>
            <RotateCw className="mr-2 h-4 w-4" />
            Transform
          </ContextMenuSubTrigger>
          <ContextMenuSubContent className="w-48">
            <ContextMenuItem onClick={onRotate90}>
              <RotateCw className="mr-2 h-4 w-4" />
              Rotate 90° CW
              <ContextMenuShortcut>R</ContextMenuShortcut>
            </ContextMenuItem>
            <ContextMenuItem onClick={onRotate90CCW}>
              <RotateCcw className="mr-2 h-4 w-4" />
              Rotate 90° CCW
              <ContextMenuShortcut>Shift+R</ContextMenuShortcut>
            </ContextMenuItem>
            <ContextMenuSeparator />
            {onFlipHorizontal && (
              <ContextMenuItem onClick={onFlipHorizontal}>
                <FlipHorizontal className="mr-2 h-4 w-4" />
                Flip Horizontal
              </ContextMenuItem>
            )}
            {onFlipVertical && (
              <ContextMenuItem onClick={onFlipVertical}>
                <FlipVertical className="mr-2 h-4 w-4" />
                Flip Vertical
              </ContextMenuItem>
            )}
            {onResetSize && (
              <>
                <ContextMenuSeparator />
                <ContextMenuItem onClick={onResetSize}>
                  <RefreshCcw className="mr-2 h-4 w-4" />
                  Reset to Original Size
                </ContextMenuItem>
              </>
            )}
          </ContextMenuSubContent>
        </ContextMenuSub>

        <ContextMenuSeparator />

        <ContextMenuItem onClick={onDelete} className="text-destructive focus:text-destructive">
          <Trash2 className="mr-2 h-4 w-4" />
          Delete
          <ContextMenuShortcut>Del</ContextMenuShortcut>
        </ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  )
}
